// The public identifier for an event: /events?e=UMSU-Welcome-Day
//
// It names the club and the event in the link itself, because that link is
// pasted into an Instagram bio, printed under a QR code and read aloud. A
// generated id (`evt_m1x8k…`) tells the person holding the poster nothing about
// whether they are about to open the right thing.
//
// DERIVED, not stored. Renaming an event changes its link, which is the
// behaviour that keeps the URL honest — a link reading UMSU-Welcome-Day for an
// event now called something else is worse than a link that moved. The older
// authored `slug` is still accepted as an alias so anything already shared keeps
// working (see `findEvent`).

/**
 * One component of the slug: words joined by hyphens, case preserved.
 *
 * Case is preserved because "UMSU" shouted back as "umsu" reads like a typo of
 * the club's own name, and the resolver is case-insensitive anyway — so keeping
 * it costs nothing and a link looks like the thing it points at.
 */
function part(s) {
  return String(s || '')
    .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')   // é → e, so the URL stays ASCII
    .replace(/[^A-Za-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

const norm = (s) => String(s || '').toLowerCase();

/** `UMSU-Welcome-Day`, or '' if there is not enough to name it with. */
export function eventSlug(e) {
  const club = part(e?.host || e?.club);
  const name = part(e?.name);
  if (!name) return '';
  return club ? `${club}-${name}` : name;
}

/**
 * The full link an attendee opens.
 *
 * Pass `all` — every event in play — and the link is guaranteed to open THIS
 * event. A club can hold two events with the same name (a Welcome Day in two
 * buildings on two days is a real thing, and live data had exactly that), and
 * they derive the same pretty slug; without the id one of them was reachable
 * only by being first in the list.
 *
 * The id rides ALONGSIDE the slug rather than replacing it, so the link still
 * says what it opens, and `findEvent` prefers the id. It is added only when the
 * slug is actually ambiguous — otherwise every link would carry an opaque id for
 * a collision that does not exist.
 */
export function eventUrl(e, origin, all) {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : 'https://www.myumaps.ca');
  const slug = eventSlug(e);
  const id = encodeURIComponent(e?.id || '');
  if (!slug) return `${base}/events?id=${id}`;
  const clash = Array.isArray(all) && all.some(
    o => o && o.id !== e?.id && norm(eventSlug(o)) === norm(slug));
  return clash ? `${base}/events?e=${slug}&id=${id}` : `${base}/events?e=${slug}`;
}

/**
 * Resolve what the URL asked for.
 *
 * Tried in order of how specific each is: an id names exactly one event and
 * never changes; the derived slug is what current links use; the authored slug
 * is the alias that keeps older ones alive. Matching is case-insensitive so a
 * link retyped in lowercase off a poster still lands.
 */
export function findEvent(events, wanted) {
  const list = Array.isArray(events) ? events : [];
  if (wanted?.id) return list.find(e => e?.id === wanted.id) || null;
  const want = norm(wanted?.slug);
  if (!want) return null;
  return list.find(e => norm(eventSlug(e)) === want)
    || list.find(e => norm(e?.slug) === want)
    || null;
}
